import { createSlice, type Draft, type PayloadAction } from "@reduxjs/toolkit";

import { EditUI } from "@/features/app/appSlice";

import {
  commitFieldChange,
  createUndoable,
  redoFieldChange,
  type UndoableCommitOptions,
  undoFieldChange,
} from "@/app/libs/undoable";
import { deepMergeKeepArrays } from "@/features/editor/components/overlay_layers/utils";
import {
  type EditorChanges,
  type GenericOverlayFeature,
  OverlayFeatureTypes,
  type Profile,
  type SliceProps,
} from "@/features/editor/types";
import type { PathDictGenericItem } from "@/libs/models/path";
import { applyChangesToState } from "./utils";

const initialState: SliceProps = {
  changes: {
    overlayFeatures: {},
  },
  filterText: null,
  overlaysEditor: {
    selectedFeatureId: undefined,
    visible: {
      [OverlayFeatureTypes.geofence]: false,
      [OverlayFeatureTypes.icon]: false,
      [OverlayFeatureTypes.model]: false,
      [OverlayFeatureTypes.text]: false,
      [OverlayFeatureTypes.poi]: false,
    },
    formErrors: {},
  },
  pathsEditor: {
    changesHistory: createUndoable<EditorChanges<PathDictGenericItem>>({}),
    selectedFeatureId: undefined,
    settings: {
      analyzeProblematicNodes: true,
      displayFloorChanges: true,
      displayNodeIds: false,
      displayOverGeojson: true,
    },
    temporaryNode: undefined,
    tool: "select",
  },
  profiles: {
    list: [],
    newName: null,
  },
};

export const EditorSlice = createSlice({
  initialState,
  name: "editor",
  reducers: {
    isolateLayer: (state, action: PayloadAction<OverlayFeatureTypes>) => {
      state.overlaysEditor.visible = Object.values(OverlayFeatureTypes).reduce(
        (acc, element) => ({
          ...acc,
          [element]: element === action.payload,
        }),
        {} as Record<OverlayFeatureTypes, boolean>,
      );
    },
    redoEditorAction: (state, action: PayloadAction<EditUI>) => {
      switch (action.payload) {
        case EditUI.paths:
          state.pathsEditor.changesHistory = redoFieldChange(
            state.pathsEditor.changesHistory,
          );
          break;
        // TODO undo-redo: implement overlay layers
        // case EditUI.overlayLayers:
        default:
          throw new Error(
            `Mode has no history configuration: ${action.payload}`,
          );
      }
    },
    removeProfileFromListById: (
      state,
      action: PayloadAction<Profile["id"]>,
    ) => {
      state.profiles.list = state.profiles.list.filter(
        (file) => file.id != action.payload,
      );
    },
    removeProfileFromListByName: (
      state,
      action: PayloadAction<Profile["name"]>,
    ) => {
      state.profiles.list = state.profiles.list.filter(
        (file) => file.name != action.payload,
      );
    },
    removeTemporalProfiles: (state, _action: PayloadAction<void>) => {
      state.profiles.list = state.profiles.list.filter(
        (file) => !file.metadata?.temporal,
      );
    },
    // Common
    resetAllChanges: (state) => {
      (
        Object.keys(state.changes) as Array<keyof SliceProps["changes"]>
      ).forEach((key) => {
        state.changes[key] = {};
      });
      state.overlaysEditor.formErrors = {};
      state.pathsEditor.changesHistory = createUndoable<
        EditorChanges<PathDictGenericItem>
      >({});
    },
    resetChanges: (state, action: PayloadAction<EditUI>) => {
      switch (action.payload) {
        case EditUI.paths:
          // TODO undo-redo: add a new present state to undoable, or replace it entirely?
          state.pathsEditor.changesHistory = createUndoable<
            EditorChanges<PathDictGenericItem>
          >({});
          break;
        case EditUI.overlayLayers:
          state.changes.overlayFeatures = {};
          state.overlaysEditor.formErrors = {};
          break;
        default:
          break;
      }
    },
    setCurrentProfileNewName: (
      state,
      action: PayloadAction<SliceProps["profiles"]["newName"]>,
    ) => {
      state.profiles.newName = action.payload;
    },
    setEditorFilterText: (
      state,
      action: PayloadAction<SliceProps["filterText"]>,
    ) => {
      state.filterText = action.payload;
    },
    setLayerVisibility: (
      state,
      action: PayloadAction<{
        featureType: OverlayFeatureTypes;
        visibility: boolean;
      }>,
    ) => {
      state.overlaysEditor.visible[action.payload.featureType] =
        action.payload.visibility;
    },
    setPathsAnalyzeProblematicNodes: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.pathsEditor.settings.analyzeProblematicNodes = action.payload;
    },
    setPathsDisplayFloorChanges: (state, action: PayloadAction<boolean>) => {
      state.pathsEditor.settings.displayFloorChanges = action.payload;
    },
    setPathsDisplayNodeIds: (state, action: PayloadAction<boolean>) => {
      state.pathsEditor.settings.displayNodeIds = action.payload;
    },
    setPathsDisplayOverGeojson: (state, action: PayloadAction<boolean>) => {
      state.pathsEditor.settings.displayOverGeojson = action.payload;
    },
    setPathsEditorSelectedFeatureId: (
      state,
      action: PayloadAction<string | undefined>,
    ) => {
      state.pathsEditor.selectedFeatureId = action.payload?.toString();
    },
    setPathsEditorTool: (
      state,
      action: PayloadAction<SliceProps["pathsEditor"]["tool"]>,
    ) => {
      state.pathsEditor.tool = action.payload;
    },
    setPathsTemporaryNode: (
      state,
      action: PayloadAction<SliceProps["pathsEditor"]["temporaryNode"]>,
    ) => {
      state.pathsEditor.temporaryNode = action.payload;
    },
    setProfilesList: (
      state,
      action: PayloadAction<SliceProps["profiles"]["list"]>,
    ) => {
      state.profiles.list = action.payload;
    },
    setFormError: (
      state,
      action: PayloadAction<{ field: string; error: string | null }>,
    ) => {
      if (action.payload.error === null) {
        delete state.overlaysEditor.formErrors[action.payload.field];
      } else {
        state.overlaysEditor.formErrors[action.payload.field] =
          action.payload.error;
      }
    },
    // OverlayLayers editor
    setSelectedFeatureId: (
      state,
      action: PayloadAction<string | undefined>,
    ) => {
      state.overlaysEditor.selectedFeatureId = action.payload;
      state.overlaysEditor.formErrors = {};
    },
    storeOverlayFeaturesChanges: (
      state,
      action: PayloadAction<EditorChanges<GenericOverlayFeature>>,
    ) => {
      Object.entries(action.payload).forEach(([id, change]) => {
        const { type } = change;

        if (type === "deleted") {
          // If the item was newly added, simply remove it
          if (state.changes.overlayFeatures[id]?.type === "new") {
            delete state.changes.overlayFeatures[id];
          } else {
            // Otherwise, mark it as deleted
            state.changes.overlayFeatures[id] = { type };
          }
        } else {
          const item = (
            change as Extract<typeof change, { type: "new" | "updated" }>
          ).item;

          const existing = state.changes.overlayFeatures[id];

          if (type == "new" && !existing) {
            // If no previous item is found, add it to the store
            state.changes.overlayFeatures[id] = {
              item: item as GenericOverlayFeature,
              type: "new",
            };
          } else if (existing?.type === "new") {
            // If the item was newly added, merge the changes but keep type "new"
            state.changes.overlayFeatures[id] = {
              item: deepMergeKeepArrays(existing.item, item),
              type: "new",
            };
          } else {
            // For updates or new entries, merge with existing data (if any)
            state.changes.overlayFeatures[id] = {
              item: deepMergeKeepArrays(existing?.item ?? {}, item),
              type: "updated",
            };
          }
        }
      });
    },

    // Paths
    storePathsChanges: (
      state,
      action: PayloadAction<{
        changes: EditorChanges<PathDictGenericItem>;
        commitOptions?: UndoableCommitOptions;
      }>,
    ) => {
      state.pathsEditor.changesHistory = commitFieldChange(
        state.pathsEditor.changesHistory,
        (draft) => applyChangesToState(action.payload.changes, draft),
        action.payload.commitOptions,
      );
    },
    toggleLayerVisibility: (
      state,
      action: PayloadAction<OverlayFeatureTypes>,
    ) => {
      state.overlaysEditor.visible[action.payload] =
        !state.overlaysEditor.visible[action.payload];
    },
    undoEditorAction: (state, action: PayloadAction<EditUI>) => {
      switch (action.payload) {
        case EditUI.paths:
          state.pathsEditor.changesHistory = undoFieldChange(
            state.pathsEditor.changesHistory,
          );
          break;
        default:
          // TODO undo-redo: implement overlay layers
          // case EditUI.overlayLayers:
          throw new Error(
            `Mode has no history configuration: ${action.payload}`,
          );
      }
    },
    unshiftProfileToList: (state, action: PayloadAction<Profile>) => {
      const alreadyExist = state.profiles.list.some(
        (file) => file?.metadata?.temporal == true && file.name == "",
      );
      !alreadyExist && state.profiles.list.unshift(action.payload);
    },
  },
});

// Action creators are generated for each case reducer function
export const {
  resetAllChanges,
  resetChanges,
  setFormError,
  setSelectedFeatureId,
  setEditorFilterText,
  storeOverlayFeaturesChanges,
  setProfilesList,
  removeTemporalProfiles,
  unshiftProfileToList,
  removeProfileFromListByName,
  removeProfileFromListById,
  setCurrentProfileNewName,
  setLayerVisibility,
  toggleLayerVisibility,
  isolateLayer,
  // Paths
  storePathsChanges,
  setPathsEditorSelectedFeatureId,
  setPathsTemporaryNode,
  setPathsAnalyzeProblematicNodes,
  setPathsDisplayNodeIds,
  setPathsDisplayFloorChanges,
  setPathsDisplayOverGeojson,
  setPathsEditorTool,
  // Undo - Redo
  undoEditorAction,
  redoEditorAction,
} = EditorSlice.actions;

export default EditorSlice.reducer;
